/**
 * BrioDrawer -- command handling, backdrop dismiss, and Escape animation
 * for drawer dialogs.
 *
 * Wraps a native <dialog class="drawer"> and listens for:
 *   - command="--dismiss" on the dialog (animated close via commandfor)
 *   - Escape key (native cancel event, intercepted to animate)
 *   - Backdrop click (always enabled -- drawers are dismissable by nature)
 *
 * It also syncs aria-expanded on every trigger that opens the drawer
 * (command="show-modal" / "show") from the dialog's [open] attribute --
 * invoker commands don't manage trigger state themselves.
 *
 * The slide-out animation targets .drawer-panel (not the dialog itself)
 * because the slide transition runs on the panel element.
 *
 * Standard commands (show-modal, close) are handled natively by the
 * browser or the command.js polyfill in brio-base -- no interception needed.
 *
 * Expected markup:
 *   <brio-drawer>
 *     <dialog class="drawer" id="my-drawer">
 *       <div class="drawer-panel">
 *         <header>
 *           <h3>Title</h3>
 *           <button class="dismiss" commandfor="my-drawer" command="--dismiss"
 *                   aria-label="Close">&times;</button>
 *         </header>
 *         <section>...</section>
 *       </div>
 *     </dialog>
 *   </brio-drawer>
 *
 * The open trigger lives outside the component and uses commandfor:
 *   <button commandfor="my-drawer" command="show-modal">Open</button>
 *
 * Programmatic API:
 *   brio.drawer.open("drawer-id");
 *   brio.drawer.close("drawer-id");
 */
import { BrioBase } from './brio-base.js';
import { animatedClose } from './animated-close.js';

class BrioDrawer extends BrioBase {
    static tag = 'brio-drawer';
    static {
        customElements.define(BrioDrawer.tag, BrioDrawer);
    }

    // Private class fields -- enforced at the language level so external
    // code can't reach in and manipulate internal state. Listeners are
    // stored on the instance so disconnectedCallback can remove the exact
    // function references it added.
    #dialog;
    #onCommand;
    #onCancel;
    #onClick;
    #openObserver;

    connectedCallback() {
        this.#dialog = this.$('dialog');
        if (!this.#dialog) return;

        // Custom command: --dismiss (animated close).
        // The command event fires directly on the dialog element.
        this.#onCommand = (e) => {
            const command = e.command || e.detail?.command;
            if (command !== '--dismiss') return;

            e.preventDefault();
            this.#closeWithPanel();
        };
        this.#dialog.addEventListener('command', this.#onCommand);

        // Escape key -- intercept to animate the panel sliding out.
        this.#onCancel = (e) => {
            e.preventDefault();
            this.#closeWithPanel();
        };
        this.#dialog.addEventListener('cancel', this.#onCancel);

        // Backdrop click -- clicking the dialog itself (outside the panel)
        // means the click landed on the backdrop.
        this.#onClick = (e) => {
            if (e.target === this.#dialog && this.#dialog.open) {
                this.#closeWithPanel();
            }
        };
        this.#dialog.addEventListener('click', this.#onClick);

        // Trigger ARIA: invoker commands don't update the trigger, so a
        // navbar hamburger wired with commandfor would sit at
        // aria-expanded="false" forever. Sync every show/show-modal
        // trigger for this dialog from its [open] attribute. A
        // MutationObserver catches every path -- native invoker
        // commands, the command.js polyfill, and programmatic
        // showModal()/close(), animated or not.
        this.#syncTriggerState();
        this.#openObserver = new MutationObserver(() => this.#syncTriggerState());
        this.#openObserver.observe(this.#dialog, {
            attributes: true,
            attributeFilter: ['open'],
        });
    }

    disconnectedCallback() {
        if (!this.#dialog) return;
        this.#dialog.removeEventListener('command', this.#onCommand);
        this.#dialog.removeEventListener('cancel', this.#onCancel);
        this.#dialog.removeEventListener('click', this.#onClick);
        this.#openObserver?.disconnect();
    }

    /**
     * Mirrors the dialog's [open] attribute onto every trigger that
     * opens it (command="show-modal" or "show"). The dismiss trigger
     * inside the panel (command="--dismiss") is excluded on purpose:
     * aria-expanded describes an open/close toggle, not a close action.
     */
    #syncTriggerState() {
        const id = this.#dialog.id;
        if (!id) return;
        const expanded = this.#dialog.open ? 'true' : 'false';
        for (const trigger of document.querySelectorAll(
            `[commandfor="${id}"][command="show-modal"], [commandfor="${id}"][command="show"]`
        )) {
            trigger.setAttribute('aria-expanded', expanded);
        }
    }

    /**
     * Animated close targeting the drawer panel for the slide transition.
     * The panel is the visible sliding element; the dialog itself is the
     * full-screen backdrop container.
     */
    #closeWithPanel() {
        const panel = this.#dialog.querySelector('.drawer-panel');
        animatedClose(this.#dialog, { transitionTarget: panel || undefined });
    }
}

// Programmatic API -- exposed via brio.drawer.open/close in brio.js.
// These are standalone functions (not on the class) because the open
// trigger is typically outside the component, referencing by ID.
export function openDrawer(id) {
    const drawer = document.getElementById(id);
    if (drawer && typeof drawer.showModal === 'function') {
        drawer.showModal();
    }
}

export function closeDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    const panel = drawer.querySelector('.drawer-panel');
    animatedClose(drawer, { transitionTarget: panel || undefined });
}
